import {
  Box,
  BoxProps,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ComponentProps, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatPercentFactor } from "@app/utils";
import {
  ChartActiveElement,
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { createPendoClassName, pendoClasses } from "@app/core/thirdParty/pendo";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarIconZoomIn, CarIconZoomOut } from "@app/components/Icons";
import { CarChartLegend } from "@app/components/ChartLegend";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { CarAssetClassDftItem } from "@app/pages/assetClassAnalysis/useAssetClassSummaryStat";

type Options = ComponentProps<typeof Chart>["options"];

type DataPoint = number[];

interface DftVersusTenYearReturnBoxAndWhiskerData {
  xLabel: string;
  yLabel: string;
  labels: string[];
  datasets: ChartDataset<"boxplot", DataPoint[]>[];
  dftEnd: number;
  priceDrivenExpectedReturn: number;
  expectedHistoricalReturn: number;
  groupedPoints: {
    fromDft: number;
    toDft: number;
    returns: number[];
  }[];
}

export const getDftVersusTenYearReturnBoxAndWhiskerData = ({
  theme,
  dftItem,
  forPdf,
}: {
  theme: Theme;
  dftItem: CarAssetClassDftItem;
  forPdf?: boolean;
}): DftVersusTenYearReturnBoxAndWhiskerData => {
  const dataPoints =
    dftItem.modelChartData.boundary_scatter_x
      ?.map((i, idx) => ({
        dft: i / 100,
        tenYearReturn:
          (dftItem.modelChartData.boundary_scatter_y?.at(idx) ?? 0) / 100,
      }))
      .sort((a, b) => a.dft - b.dft) ?? [];

  const dftValues = dataPoints.map((d) => d.dft);
  const tenYearReturnValues = dataPoints.map((d) => d.tenYearReturn);

  const binSize = 0.1;
  const minDft = Math.min(...dftValues);
  const maxDft = Math.max(...dftValues);

  const startBin = Math.floor(minDft / binSize) * binSize;
  const endBin = Math.ceil(maxDft / binSize) * binSize;

  const bins: number[] = [];

  for (let b = startBin; b <= endBin + binSize / 2; b += binSize) {
    bins.push(b);
  }

  const dataPointsWithBins = dftValues.map((dist, i) => {
    const binIndex = bins.findIndex(
      (bin) => dist >= bin && dist < bin + binSize,
    );
    const fromDft = bins.at(binIndex) ?? 0;
    return {
      distance: dist,
      returns: tenYearReturnValues[i],
      fromDft,
    };
  });

  const groupedPoints = Array.from(
    Map.groupBy(dataPointsWithBins, (i) => i.fromDft).entries(),
  )
    .map(([fromDft, v]) => ({
      fromDft,
      toDft: fromDft + binSize,
      returns: v.flatMap((i) => i.returns),
    }))
    .sort((a, b) => a.fromDft - b.fromDft);

  const dftEnd = (dftItem.modelChartData.label_end_dft ?? 0) / 100;

  const result: DftVersusTenYearReturnBoxAndWhiskerData = {
    labels: groupedPoints.map((i) =>
      formatPercentFactor(i.fromDft + binSize / 2),
    ),
    datasets: [
      {
        type: "boxplot",
        id: "data",
        data: groupedPoints.map((i) => i.returns),
        medianColor: "#00000000",
        backgroundColor: (v) => {
          const point = groupedPoints.at(v.dataIndex);
          const inDft =
            point && dftEnd >= point.fromDft && dftEnd < point.toDft;
          return inDft
            ? theme.palette.chartsColor.orange
            : theme.palette.chartsColor.blue;
        },
        outlierRadius: 0,
        itemRadius: 0,
        meanRadius: 0,
        borderColor: theme.palette.softBlack,
        borderWidth: 1,
        // categoryPercentage: 1,
        // barPercentage: 0.8,
      },
    ],
    xLabel: `${dftItem.name}: Min. and Max. 10 Year Forward Real Returns Vs. Distance from Trend`,
    yLabel: "Compound Annual Return",
    dftEnd,
    priceDrivenExpectedReturn:
      (dftItem.modelChartData.label_expected_return ?? 0) / 100,
    expectedHistoricalReturn: dftItem.realStaticExpectedReturn ?? 0,
    groupedPoints,
  };

  return result;
};

interface ChartDftVersusTenYearReturnBoxAndWhiskerProps {
  sx?: BoxProps["sx"];
  dftItem: CarAssetClassDftItem;
  isWhiteContext: boolean;
}

export const ChartDftVersusTenYearReturnBoxAndWhisker = ({
  sx,
  dftItem,
  isWhiteContext,
}: ChartDftVersusTenYearReturnBoxAndWhiskerProps) => {
  const theme = useTheme();

  const {
    labels,
    datasets,
    yLabel,
    xLabel,
    dftEnd,
    groupedPoints,
    priceDrivenExpectedReturn,
    expectedHistoricalReturn,
  } = useMemo(
    () =>
      getDftVersusTenYearReturnBoxAndWhiskerData({
        theme,
        dftItem,
      }),
    [theme, dftItem],
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "ChartDftVersusTenYearReturnBoxAndWhisker",
    // activeElement,
  });

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    DataPoint[],
    unknown
  > | null>();

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const dataPoint = datasets.at(datasetIndex)?.data?.at(dataIndex) ?? [];
    console.log({ datasetIndex, dataIndex, dataPoint });
    const minValue = Math.min(...dataPoint);
    const maxValue = Math.max(...dataPoint);

    return (
      <>
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            maxWidth: 300,
            gap: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {`Observations = ${dataPoint?.length ?? "-"}`}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {`Min. 10 Year Forward Return: ${formatPercentFactor(minValue)}`}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {`Max. 10 Year Forward Return: ${formatPercentFactor(maxValue)}`}
          </Typography>
        </Box>
      </>
    );
  };

  const dftIndex = groupedPoints.findIndex(
    (gp) => dftEnd >= gp.fromDft && dftEnd < gp.toDft,
  );

  const dftPoint = groupedPoints.at(dftIndex);

  const dftShift = dftEnd - (dftPoint?.fromDft ?? 0);

  const options = useMemo(() => {
    const result: Options = {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      layout: {
        padding: {
          right: 1, // for some reason right border is cropped, so add right padding to fit it in image
        },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
          },
          grid: {
            display: false,
          },
          type: "category",
        },
        y: {
          offset: false,
          type: "linear",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },

          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: (value) =>
              typeof value === "number" ? formatPercentFactor(value, 1) : value,
          },
          grid: ChartUtils.getGridOptions({ theme, highlightZero: true }),
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
        tooltip: tooltipPlugin,
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          zoom: {
            pinch: {
              enabled: true,
            },
            mode: "x",
          },
        },
        ...{
          dftLine: {
            dftIndex,
            dftShift,
            priceDrivenExpectedReturn,
            expectedHistoricalReturn,
          },
        },
      },
    };
    return result;
  }, [
    theme,
    tooltipPlugin,
    yLabel,
    isWhiteContext,
    dftIndex,
    dftShift,
    priceDrivenExpectedReturn,
    expectedHistoricalReturn,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: 500,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", zIndex: 1, alignItems: "flex-end" }}>
        <Typography
          variant="par03SemiBold"
          sx={{ fontSize: "19px", mb: 0.5, ml: 10 }}
        >
          {xLabel}
          <CarTooltipBox
            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
            className={
              pendoClasses.assetClassAnalysisDftMinMaxTenYearForwardRealReturn
            }
          />
        </Typography>

        <IconButton
          sx={{ ml: "auto" }}
          size="small"
          onClick={() => {
            chartRef.current?.zoom(1.5, "zoom");
          }}
        >
          <CarIconZoomIn fontSize="large" color="primary" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            chartRef.current?.zoom(-1.5, "zoom");
          }}
        >
          <CarIconZoomOut fontSize="large" color="primary" />
        </IconButton>
      </Box>
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          /* Some bug: can't specify height on chart because it starts to grow, so put chart in the box */
          height: 500,
          mt: -1,
          position: "relative",
        }}
      >
        <CarTooltipBox
          sx={{ position: "absolute", left: 5, top: 40 }}
          className={pendoClasses.assetClassAnalysisDftCompoundAnnualReturn}
        />
        <Chart
          ref={chartRef}
          onDoubleClick={() => {
            chartRef.current?.resetZoom();
          }}
          type="boxplot"
          datasetIdKey="id"
          data={{ labels, datasets }}
          options={options}
          plugins={[
            ChartPlugins.roundedBackground,
            ChartPlugins.rangeHighlight,
            {
              id: "dftLine",
              afterDraw: (chart, args, options) => {
                const {
                  dftIndex,
                  dftShift,
                  priceDrivenExpectedReturn,
                  expectedHistoricalReturn,
                } = options;

                const ctx = chart.ctx;
                const chartArea = chart.chartArea;

                const percentWidth =
                  (chart.scales.x.getPixelForValue(1) -
                    chart.scales.x.getPixelForValue(0)) /
                  10;

                const shitX = (Math.floor(dftShift * 100) - 5) * percentWidth;
                const dftCenterX = chart.scales.x.getPixelForValue(dftIndex);

                ctx.save();

                // dft line
                ctx.strokeStyle = theme.palette.chartsColor.green;
                ctx.beginPath();
                ctx.setLineDash([4, 4]);
                ctx.lineWidth = 3;
                ctx.moveTo(dftCenterX + shitX, chartArea.top);
                ctx.lineTo(
                  dftCenterX + shitX,
                  chartArea.top + chartArea.height,
                );
                ctx.stroke();

                // price driven expected return
                const yPriceDrivenExpectedReturn =
                  chart.scales.y.getPixelForValue(priceDrivenExpectedReturn);

                ctx.strokeStyle = theme.palette.chartsColor.aqua;
                ctx.beginPath();
                ctx.setLineDash([4, 4]);
                ctx.lineWidth = 3;
                ctx.moveTo(chartArea.left, yPriceDrivenExpectedReturn);
                ctx.lineTo(chartArea.right, yPriceDrivenExpectedReturn);
                ctx.stroke();

                // price driven expected return
                const yExpectedHistoricalReturn =
                  chart.scales.y.getPixelForValue(expectedHistoricalReturn);

                ctx.strokeStyle = theme.palette.chartsColor.red;
                ctx.beginPath();
                ctx.setLineDash([4, 4]);
                ctx.lineWidth = 3;
                ctx.moveTo(chartArea.left, yExpectedHistoricalReturn);
                ctx.lineTo(chartArea.right, yExpectedHistoricalReturn);
                ctx.stroke();

                ctx.restore();
              },
            },
          ]}
        />
      </ChartTooltip>

      <CarChartLegend
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        labelVariant="caption"
        items={[
          {
            label: (
              <>
                Historical Forward Returns Based off of the given distance of
                trend
                {/* <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftExpectedReturnTrendLine
                  }
                /> */}
              </>
            ),
            color: theme.palette.chartsColor.blue,
            maxWidth: 310,
          },
          {
            label: (
              <>
                Historical forward returns for current distance of trend
                {/* <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftUpperAndLowerBounds
                  }
                /> */}
              </>
            ),
            color: theme.palette.chartsColor.orange,
            maxWidth: 280,
          },
        ]}
      />
      <CarChartLegend
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
        }}
        labelVariant="caption"
        items={[
          {
            label: (
              <>
                Current distance from trend
                {/* <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftAssetClassTenYearRealReturn
                  }
                /> */}
              </>
            ),
            color: theme.palette.chartsColor.green,
            isDottedLine: true,
            maxWidth: 300,
          },
          {
            label: (
              <>
                Price-Driven Expected Return
                {/* <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftAssetClassTenYearRealReturn
                  }
                /> */}
              </>
            ),
            color: theme.palette.chartsColor.aqua,
            isDottedLine: true,
            maxWidth: 300,
          },
          {
            label: (
              <>
                Expected historical return
                {/* <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftAssetClassTenYearRealReturn
                  }
                /> */}
              </>
            ),
            color: theme.palette.chartsColor.red,
            isDottedLine: true,
            maxWidth: 300,
          },
        ]}
      />
    </Box>
  );
};
